import { themeColors } from "../../themeColors";
import BackButton from "@/components/Common/BackButton";
/**
 * @file   src\pages\WantadDetails.js
 * @brief  This file is responsible for showing want ad details page
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect, useLayoutEffect } from "react";
import Button from "react-bootstrap/Button";
import Dropdown from "react-bootstrap/Dropdown";
import Form from "react-bootstrap/Form";
import AdIntroIcon from "../../images/ad-intro-icon.svg";
import PlaceHolderIcon from "../../images/dpdown-placeholder-icon.svg";
import LakeViewIcon from "../../images/lake-view-icon.svg";
import ConsumerIcon from "../../images/consumer-icon.svg";
import FinancingIcon from "../../images/financing-icon.svg";
import DisplacedFired from "../../images/v2/displaced-fire.svg";
import PrResIcon from "../../images/primary-residence-icon.svg";
import ShareIcon from "../../images/share-icon.svg";
import MoreIcon from "../../images/more-icon.svg";
import PaperIcon from "../../images/paper-icon.svg";
import SeoHead from "../../components/SeoHead";
import ReportWantAdModal from "../../components/MemberWantAd/ReportWantAdModal";
import ViewWantAdIntroductions from "../../components/Introduction/ViewWantAdIntroductions";
import ViewIntroductionModal from "../../components/Introduction/ViewIntroductionModal";
import { usePrevious } from "../../hooks/usePrevious";
import ArchiveModal from "../../components/MyWantAd/ArchiveModal";
import PremiumPlanModal from "../../components/PlanUpgrade/PremiumPlanModal";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import {
  getPropertyIcon,
  getTooltipText,
  getCombinedText,
} from "../../helpers/getPropertyIcons";
import AgentProfileCard from "../../components/Common/AgentProfileCard";
import { useWantAdMlsMatches } from "@/hooks";
import { useQueryClient } from "@tanstack/react-query";
import {
  BUY,
  LONG_RENT,
  SHORT_RENT,
  LOT_LAND,
  WANT_AD_KEY,
  USER_INFO_KEY,
  INVENTORY_KEY,
  IS_MULTI_FAMILY,
  IS_LAND,
  INVESTOR_MEMBER_PLAN,
  USER_TYPE_AGENT,
  NICE_2_HAVES_CATEGORIES_MASTER_LIST,
  NICE_2_HAVES_LOT_LAND_CATEGORIES,
} from "../../constants/common";
import ShareYourAdModal from "../../components/MemberWantAd/v2/ShareYourAdModal";
import ShareMyWantAdSmallModal from "../../components/MemberWantAd/ShareMyWantAdSmallModal";
import {
  loadTimeLineHtml,
  getLocalStorage,
  setLocalStorage,
  getUserId,
  loggedInUser,
  getUserType,
  loadPreferredLocationHtml,
  locationDisplay,
  loadBuyerAgreementHtml,
  saveTutorialStatusToLocal,
  checkTutorialDisplayStatus,
} from "../../helpers/v2/common";
import { pageURLs } from "../../constants/pageURLs";
import {
  useNavigate,
  useSearchParams,
  NavLink,
  useParams,
  useLocation,
} from "react-router-dom";
import {
  favouriteWantAd,
  getAgentsList,
  getWantAdById,
  unFavouriteWantAd,
  archiveWantAd,
  unArchiveWantAd,
  deleteWantAd,
  getWantAdByIdPublic,
  getBoundaryCoordinates,
} from "../../services/v2/wantAdService";
import { convertToNumber } from "../../helpers/validations";
import SendIntroduction from "../../components/Introduction/SendIntroduction";
import SignedOutIntroCTA from "../../components/Introduction/SignedOutIntroCTA";
import HearttIcon from "../../images/heart-icon.svg";
import HearttIconFilled from "../../images/heart-icon-filled.svg";
import { getIntroductions } from "../../services/introductionService";
import { getIntroductionDetaisById } from "../../services/introductionService";
import AddToClientWantAdModal from "../../components/MemberWantAd/AddToClientWantAdModal";
import { handleTextBoxChange } from "../../actions/common";
import { toast } from "react-toastify";
import DeleteModal from "../../components/Common/DeleteModal";
import {
  buildWantAdMustHavesHTML,
  loadInvestmentCriteria,
} from "../../helpers/v2/createWantAd";
import WantadDetailsSkeleton from "../WantadDetailsSkeleton";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import AgentProfileViewModal from "../../components/Introduction/AgentProfileViewModal";
import MinCapIcon from "../../images/minimum-icn.svg";
import userProfile from "../../appState/userProfile";
import { getUserPlanDetails } from "../../services/userprofileService";
import ShareCard from "../../components/MemberWantAd/v2/ShareCard";
import MapBoxComponent from "../../components/Common/MapboxComponent";
import { shareWantAdSocial, syndicateWantAdListing } from "../../services/v2/wantAdService";
import NoWantAdFoundModal from "../../components/MemberWantAd/v2/NoWantAdFound";
import JoyRide, { STATUS } from "react-joyride";
import IntroductionTutorialIconStep1 from "../../images/tut-property-icon.svg";
import IntroductionTutorialIconStep2 from "../../images/tut-askingprice-icon.svg";
import IntroductionTutorialIconStep3 from "../../images/tut-custommsg-icon.svg";
import IntroductionTutorialIconStep4 from "../../images/tut-send-icon.svg";
import DocumentationTutorial from "../../components/Tutorial/DocumentationTutorial";
import { useAppConfig } from "@/appConfig";
import {
  SelectDropDown,
  MatchesSkeleton,
  SendMessage,
} from "@/components";
import { PageWrapper } from "@/components/PageWrapper";
import SyndicateListingModal from "../../components/MyWantAd/SyndicateListingModal";
import InventorySuccessModal from "../../components/Inventory/CreateInventory/InventorySuccessModal";
import useLoading from "@/hooks/useLoading";
import { useMLSAgentAtGitchaRedirect } from "@/hooks/useMLSAgentAtGitchaRedirect";
import useAgentGitchaRestrictions from "@/hooks/useAgentGitchaRestrictions";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import MatchesFeatureModal from "../../components/Common/MatchesFeatureModal";
import MlsMatchCard from "@/components/MlsListings/MlsMatchCard";
import "@/styles/desktop/general.scss";
import "@/styles/bls-detail-meta.scss";
import { openModal } from "@/reducers/modals";

//Tutorial tooltip
const TutorialToolTip = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  tooltipProps,
  size,
  isLastStep,
  skipProps,
}) => (
  <>
    <div {...tooltipProps}>
      <DocumentationTutorial
        continuous={continuous}
        index={index}
        step={step}
        backProps={backProps}
        skipProps={skipProps}
        primaryProps={primaryProps}
        isLastStep={isLastStep}
        size={size}
        finalStepBtn={"Got It!"}
      />
    </div>
  </>
);

const tutorialSteps = [
  {
    target: "#propertySelection",
    disableBeacon: true,
    placement: "left",
    title: "Choose a property to share",
    icon: IntroductionTutorialIconStep1,
    content:
      "If you have multiple properties, you can swap out which one you want to introduce.",
    hideCloseButton: true,
    showProgress: true,
    showSkipButton: true,
  },
  {
    target: ".property-price",
    disableBeacon: true,
    placement: "bottom",
    title: "Adjust asking price",
    icon: IntroductionTutorialIconStep2,
    content:
      "This is just conversation starter. So try not to overthink it. You can always change it later.",
    hideCloseButton: true,
    showProgress: true,
    showSkipButton: true,
  },
  {
    target: ".property-message",
    disableBeacon: true,
    placement: "top",
    title: "Write a custom message",
    icon: IntroductionTutorialIconStep3,
    content:
      "This is your first chance to tell them why you think your property is a perfect match.",
    hideCloseButton: true,
    showProgress: true,
    showSkipButton: true,
  },
  {
    target: ".sendMessageBtn",
    disableBeacon: true,
    placement: "left",
    title: "Review and send",
    icon: IntroductionTutorialIconStep4,
    content:
      "You'll get another chance to review your introduction before you finalize and send.",
    hideCloseButton: true,
    showProgress: true,
  },
]

const OtherConcessionsSection = ({ adType, adDetails }) => {

  const shouldDisplayOtherConcessionsSection = (adType == BUY || adDetails?.prprtyType == IS_LAND) && adDetails?.isAgentCreated == 1;

  const otherConcessions = adDetails?.otherConcessions;

  if (!shouldDisplayOtherConcessionsSection || !otherConcessions) return null;

  return (
    <div className="buyer-info">
      <h2 className="wanted-details-head">
        Requested Concessions From Seller
      </h2>
      <ul>
        {adDetails?.otherConcessions && <li>{adDetails?.otherConcessions}</li>}
      </ul>
    </div>
  )
}

const WantadDetails = (props) => {

  const navigate = useNavigate();

  const location = useLocation();
  const isFromConfirmationPage = location.state?.isFromConfirmationPage;

  const options = [
    { value: "most_relevant", label: "Most relevant" },
    { value: "most_recent", label: "Most recent" },
  ];

  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const id = searchParams.get("wantAdId") || params?.wantAdId;

  const [showShareModal, setShareModal] = useState(false);
  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);

  const [showSuccessModal, setSuccessModal] = useState(false);
  const [showSyndicateSuccessModal, setSyndicateSuccessModal] = useState(false);
  const [showReportModal, setReportModal] = useState(false);
  const handleClose = () => setShareModal(false);
  const [adDetails, setAdDetails] = useState();
  const [currPage, setCurrPage] = useState(1);
  const [showViewIntroductionModal, setViewIntroductionModal] = useState(false);
  const adType = _.get(adDetails, "adType", "");
  const createdUserTypeId = _.get(adDetails, "userTypeId", "");
  const propertyType = convertToNumber(_.get(adDetails, "prprtyType", ""));
  const adTypeBasedOnProperty = propertyType === 6 ? LOT_LAND : adType;
  const nice2Have = _.get(adDetails, "nice2Have", []);
  const [introductionList, setIntroductionList] = useState([]);
  const [introCount, setIntroCount] = useState(0);
  const [polygonCoordinates, setPolygonCoordinates] = useState([]);
  const wantAd = getLocalStorage(WANT_AD_KEY);
  const userType = getUserType();

  //This variable is used to change intro title and description, if this page reaches from home feed.
  const isFromHomeFeed = id
    ? true
    : _.get(wantAd, "isFromHomeFeed", false);

  //This variable is used to change intro title and description, if this page reaches from all introductions.
  const isFromAllIntroduction = _.get(wantAd, "isFromAllIntroduction", false);
  const wantAdId = _.get(wantAd, "wantAdId", null);
  const introId = _.get(wantAd, "introId", null);
  const isEdit = _.get(wantAd, "isEdit", false);
  const WantCreatedUserId = _.get(adDetails, "userId", false);
  const loggedInUserId = getUserId();
  const [introDetails, setIntroDetails] = useState([]);
  const [deleteModalShow, setDeleteModalShow] = useState(false); //
  const WantCreatedUserName =
    _.get(adDetails, "userFirstName", "") +
    " " +
    _.get(adDetails, "userLastName", "");
  const userCompany = _.get(adDetails, "userCompany", "");
  const wantAdClientId = _.get(adDetails, "clientId", "");
  const lat = _.get(adDetails, "lat");
  const lng = _.get(adDetails, "lng");
  const mapRadius = _.get(adDetails, "mapRadius", null);
  const drawnPolygon = _.get(adDetails, "drawnPolygon", null);
  const userAvatar = _.get(adDetails, "userAvatar", "");
  const wantCreatedFirstName = _.get(adDetails, "userFirstName", "");
  const isFavourite = _.get(adDetails, "isFavourite", false);
  const propertySubTypes = _.get(adDetails, "prptySubTypes", []);
  const handleIntroDetailModal = (modalState) =>
    setViewIntroductionModal(modalState);
  const INTRO_LIST_PAGE_LIMIT = 3;
  const previousPage = usePrevious(currPage);
  const isLoadIntroductionList =
    (isFromHomeFeed &&
      WantCreatedUserId &&
      WantCreatedUserId === loggedInUserId) ||
    (introId ? true : false);
  const [isFavouriteStatus, setIsFavourite] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [modalShow, setModalShow] = useState(false); //
  const [searchText, setSearchText] = useState(""); //
  const [results, setResults] = useState([]);
  const [reloadAgents, setReloadAgents] = useState(0);
  const user = loggedInUser();
  const sessionData = getLocalStorage(USER_INFO_KEY);
  const consumerType = adType == BUY ? "Buyer" : "Renter";
  const [archiveModalShow, setArchiveModalShow] = useState(false); //
  const [archiveStatus, setArchiveStatus] = useState(false); //
  const [reload, setReload] = useState(0);
  const [isWantAdCreated, setWantAdCreated] = useState(false);

  const [skeltonLoad, setSkeltonLoad] = useState(true); //
  const [noWantAdFound, setNoWantAdFound] = useState(false);
  const pageTopRef = useRef(null);

  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );

  const [showAgentProfileViewModal, setAgentProfileViewModal] = useState(false);
  const agentProfileViewModalClose = () => setAgentProfileViewModal(false);
  const purchaseType = convertToNumber(_.get(adDetails, "prchTypeId", ""));
  const agentSlug = _.get(adDetails, "userSlug", "");

  const fireDisplaced = _.get(adDetails, "fireDisplaced", "");
  const zipcodes = _.get(adDetails, "zipCode", []);
  const isSyndicatedToPublic = _.get(adDetails, "syndicateToThePublic", false);
  const [wantAdCreatedUserPlan, setWantAdCreatedUserPlan] = useState("");

  const isMyWantAd = user?.userInfo?.userId === adDetails?.userId;

  const { flags, tenant } = useAppConfig();

  const isBLSTenant = !!tenant?.id && tenant.id !== "default";
  const blsMlsName =
    (tenant?.head?.title || "").replace(/ - Buyer Listing Service$/i, "") ||
    "MLS";
  const blsReference = _.get(adDetails, "blsReference", null);
  const blsPostedDate =
    _.get(adDetails, "publishedOn", null) ||
    _.get(adDetails, "createdOn", null);
  const blsPostedLabel = blsPostedDate
    ? new Date(blsPostedDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

  const tabInitial = searchParams.get("tabKey");
  const [tabKey, setTabKey] = useState(tabInitial || "Want-Listing Details");

  // Matching-processor lifecycle: 'processing' while the backend scores this
  // want-ad, 'complete'/null otherwise. Seeded from the detail response, then
  // flipped live by the SSE 'matchingComplete' signal.
  const [matchStatus, setMatchStatus] = useState(null);
  const isMatchingInProgress = matchStatus === "processing";
  const prevMatchStatusRef = useRef(null);

  const queryClient = useQueryClient();
  const { sseData } = useSelector((state) => state.sseData);
  // Ignore whatever SSE event is already in Redux at mount (a previous
  // 'matchingComplete' lingers there). Only events that arrive afterwards are
  // fresh — otherwise a stale event would instantly clear the in-progress state.
  const seenSseRef = useRef(sseData);

  const [sortType, setSortType] = useState(options[0].value);

  const [shareUrl, setShareUrl] = useState("");
  const [fbUrl, setFbUrl] = useState("");

  const tutorialPage = 5; //Want add tutorial
  let intoLinkDisplayStatus = checkTutorialDisplayStatus(tutorialPage);
  const [showIntroLink, setIntroLink] = useState(intoLinkDisplayStatus);
  const [showIntroTutorialModal, setIntroTutorialModal] = useState(false);
  const IntroTutorialModalClose = () => setIntroTutorialModal(false);
  const [displayTutorial, setDisplayTutorial] = useState(false);
  const [showSyndicateModal, setShowSyndicateModal] = useState(false);

  const { startLoading, stopLoading } = useLoading()

  const {
    mlsMatches,
    totalMlsMatches,
    hasNextMlsPage,
    fetchNextMlsPage,
    isMatchesLoading,
    isFetchingNextMlsPage,
  } = useWantAdMlsMatches({
    wantAdId: adDetails?.wantAdId,
    sortType,
    // Fetch once the want-ad is loaded and matching has finished, so the tab
    // label shows the real count even before the user opens the Matches tab.
    enabled: isMyWantAd && !!adDetails?.wantAdId && !isMatchingInProgress,
  });

  //Tutorial documentation section
  //Defining tutorial steps
  const acceptTutorial = () => {
    setIntroTutorialModal(false);
    setDisplayTutorial(true);
  };

  //Tutorial status call back
  const handleJoyrideCallback = async (data) => {
    const { action, index, status, type, lifecycle } = data;
    if (
      [STATUS.FINISHED, STATUS.SKIPPED].includes(status) &&
      lifecycle == "complete"
    ) {
      setDisplayTutorial(false);
      setIntroLink(false);
      saveTutorialStatusToLocal(tutorialPage); //Save status
    }
  };

  const generateShareLink = async () => {
    if (!wantAdId || !loggedInUserId) {
      setShareUrl("");
      setFbUrl("");
      setImageUrl("");
      setShareModal(false);
      return;
    }

    try {
      const fbPayload = {
        wantAdId: _.get(wantAd, "wantAdId", wantAdId),
        platform: "facebook",
      };
      const response = await shareWantAdSocial(fbPayload);
      setShareUrl(response?.result?.shareUrl || "");
      setFbUrl(response?.result?.facebookShareUrl || "");
      setImageUrl(response?.result?.imageUrl || "");
    } catch (err) {
      console.error("Error generating share links:", err);
      setShareUrl("");
      setFbUrl("");
      setImageUrl("");
      setShareModal(false);
    }
  };

  const [isVertical, setIsVertical] = useState(false);
  //check if video is vertical
  useLayoutEffect(() => {
    const videoElement = document.getElementById("videoPlayer");
    if (videoElement) {
      const { videoWidth, videoHeight } = videoElement;
      setIsVertical(videoHeight > videoWidth);
    }
  });

  useEffect(() => {
    generateShareLink();
  }, [wantAdId]);


  const PremiumPlanModalClose = () => setPremiumPlanModal(false);
  const PremiumPlanModalShow = () => setPremiumPlanModal(true);
  useEffect(() => {
    if (id) {
      setLocalStorage(WANT_AD_KEY, { wantAdId: id });
    }
  }, [id]);

  const isPremiumUser = userDetailsFromStore[0]?.period !== 1 && userType === 2;

  useEffect(() => {
    getWantAdDetails(wantAdId);
    getCreatedUserPlan(wantAdId);
  }, [reload]);

  useEffect(() => {
    window.scrollTo(0, 0); // land at the very top of the page
    const showSuccess = _.get(wantAd, "showSuccess", null);
    if (showSuccess && !isFromHomeFeed && !isFromAllIntroduction) {
      setSuccessModal(true);
    } else if (
      !isFromHomeFeed &&
      !isFromAllIntroduction &&
      !introId &&
      WantCreatedUserId === loggedInUserId
    ) {
      setShareModal(true);
      setWantAdCreated(true);
    }
    getWantAdDetails(wantAdId);
  }, [
    introId,
    wantAdId,
    isFromHomeFeed,
    isFromAllIntroduction,
    WantCreatedUserId,
    loggedInUserId,
  ]);

  useEffect(() => {
    setIsFavourite(isFavourite);
  }, [adDetails]);

  useEffect(() => {
    if (adDetails?.matchStatus !== undefined) {
      setMatchStatus(adDetails.matchStatus || null);
    }
  }, [adDetails?.matchStatus]);

  // Fire a success toast only on the processing → complete transition (not on a
  // cold load that's already complete), so the user is told their matches are in.
  useEffect(() => {
    if (prevMatchStatusRef.current === "processing" && matchStatus === "complete") {
      toast.success("Your matches are ready");
    }
    prevMatchStatusRef.current = matchStatus;
  }, [matchStatus]);

  // Live finish: SSE 'matchingComplete' for this want-ad flips the tab and
  // refetches the count without a page refresh.
  useEffect(() => {
    if (sseData === seenSseRef.current) return; // stale / already handled
    seenSseRef.current = sseData;
    const payload = sseData?.payload;
    if (
      payload?.messageType === "matchingComplete" &&
      adDetails?.wantAdId &&
      Number(payload?.subjectId) === Number(adDetails.wantAdId)
    ) {
      setMatchStatus("complete");
      queryClient.invalidateQueries({
        queryKey: ["wantAdMlsMatches", adDetails.wantAdId],
      });
    }
  }, [sseData, adDetails?.wantAdId, queryClient]);

  //Fetch wantad details
  const getWantAdDetails = async (wantAdId) => {
    if (wantAdId && loggedInUserId) {
      const wantAdid = params.wantAdId;
      await getWantAdById(wantAdid, loggedInUserId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            setSkeltonLoad(false);
            setAdDetails(responseResult);
            setLocalStorage(WANT_AD_KEY, {
              ...getLocalStorage(WANT_AD_KEY),
              wantAdType: responseResult?.adType,
              wantAdId: responseResult?.wantAdId,
            });
          } else {
            setNoWantAdFound(true);
            setSkeltonLoad(false);
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    } else {
      const wantAdId = params.wantAdId;
      await getWantAdByIdPublic(wantAdId, loggedInUserId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            setSkeltonLoad(false);
            setAdDetails(responseResult);
            setLocalStorage(WANT_AD_KEY, {
              ...getLocalStorage(WANT_AD_KEY),
              wantAdId: responseResult?.wantAdId,
              wantAdType: responseResult?.adType,
            });
          } else {
            setNoWantAdFound(true);
            setSkeltonLoad(false);
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
      // navigate(pageURLs.home);
    }
  };

  // Safety net: if the SSE 'matchingComplete' signal is missed, re-poll the
  // detail status while processing so the tab never stays stuck disabled.
  useEffect(() => {
    if (!isMatchingInProgress || !adDetails?.wantAdId) return;
    const INTERVAL = 6000;
    const MAX = 90000;
    let elapsed = 0;
    const timer = setInterval(() => {
      elapsed += INTERVAL;
      if (elapsed >= MAX) {
        setMatchStatus("complete");
        clearInterval(timer);
        return;
      }
      getWantAdDetails(adDetails.wantAdId);
    }, INTERVAL);
    return () => clearInterval(timer);
  }, [isMatchingInProgress, adDetails?.wantAdId]);


  //Syndicate want ad
  const handleSyndicateWantAd = async (wantAdId) => {
    setShowSyndicateModal(false);
    startLoading('syndication-want-ad')
    try {
      const syndicatedWantAd = await syndicateWantAdListing(wantAdId)
      if (syndicatedWantAd.errorCode === 0 && syndicatedWantAd.result) {
        setSyndicateSuccessModal(true);
        setReload(reload + 1);
      }

    } catch (err) {
      toast.error("Something went wrong while syndicating want listing. Please try again later.", { autoClose: 5000 });
    } finally {
      stopLoading('syndication-want-ad')
    }
  }

  //To fetch current subscription plan of user who has created wantad
  const getCreatedUserPlan = async (wantAdId) => {
    if (wantAdId) {
      await getUserPlanDetails({ wantAdId: wantAdId })
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            setWantAdCreatedUserPlan(responseResult[0]?.PlanName);
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  const updateIntroPageOnScroll = () => {
    setCurrPage((prevState) => prevState + 1);
  };

  useEffect(() => {
    isLoadIntroductionList && getIntroductionList(currPage, wantAdId);
  }, [currPage, introId, isLoadIntroductionList]);

  //Get introductions received against the wantad
  const getIntroductionList = async (page, wantAdId) => {
    const userId = _.get(getLocalStorage(USER_INFO_KEY), "userInfo.userId", "");
    const reqPayload = {
      wantAdId: wantAdId,
      page: previousPage === currPage ? 1 : page,
      limit: INTRO_LIST_PAGE_LIMIT,
      isAccept: "1",
    };
    await getIntroductions(reqPayload).then((response) => {
      if (response) {
        const list = _.get(response, "result.List", null);
        const count = _.get(response, "result.Count", "");
        if (response.errorCode === 0) {
          if (!previousPage || previousPage === currPage) {
            setIntroductionList(list); // Loading new introduction list when selecting each introduction. So not appending with the existing list.
          } else if (previousPage !== currPage) {
            setIntroductionList([...introductionList, ...list]);
          }
          setIntroCount(count);
        } else {
          console.log(`####error`);
        }
      }
    });
  };

  // To show the share wantad popup
  const handleOnShare = () => {

    if (!shouldRestrictAgent) {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
      return;
    }

    setWantAdCreated(false);
    setShareModal(true);
  };

  const showShareModalPopup = () => {
    setShareModal(true);
    setSuccessModal(false);
    updateLocalStorage();
  };

  /**
   *  Get introduction details by passsing introduction id
   */
  const getIntroductionDetails = async (introId, IsAccept = 0) => {
    if (IsAccept == 1 || IsAccept == true) {
      setLocalStorage(INVENTORY_KEY, {
        introductionId: introId,
        adType: adType,
      });
      navigate(pageURLs.viewIntroducedInventory);
    } else {
      handleIntroDetailModal(true);
      if (introId) {
        await getIntroductionDetaisById(introId)
          .then((response) => {
            const responseResult = _.get(response, "result", null);
            if (response.errorCode === 0 && responseResult) {
              setIntroDetails(responseResult);
            } else {
              console.log("Something went wrong");
            }
          })
          .catch((err) => {
            console.log("Error", err);
          });
      } else {
      }
    }
  };

  const handleSuccessModalClose = () => {
    setSuccessModal(false);
    updateLocalStorage();
  };

  const handleReportPopup = (isShow) => setReportModal(isShow);

  const updateLocalStorage = () => {
    const wantAd = getLocalStorage(WANT_AD_KEY);
    const wantAdId = _.get(wantAd, "wantAdId", null);
    setLocalStorage(WANT_AD_KEY, { wantAdId: wantAdId });
  };

  const loadEachNiceToHave = (section) => {
    const selectedItems =
      nice2Have && nice2Have.filter((item) => item.tab === section);
    return loadNiceToHaveList(selectedItems, section.charAt(0));
  };

  const finalNiceToHaveList = () => {
    const niceToHaveMasterArray = adTypeBasedOnProperty === LOT_LAND ?
      NICE_2_HAVES_LOT_LAND_CATEGORIES.map((item) => item.category) :
      NICE_2_HAVES_CATEGORIES_MASTER_LIST.map((item) => item.category);

    const finalList =
      niceToHaveMasterArray &&
      niceToHaveMasterArray.map((nh) => {
        return loadEachNiceToHave(nh);
      });
    return finalList;
  };
  /**
   * Navigate to edit wantad on selecting edit option from dropdown menu
   */
  const onEdit = () => {
    if (userType == 2) {
      navigate(`${pageURLs.editWantAdForAgents}/${adDetails?.wantAdId}`, { state: { data: adDetails } });
    } else {
      navigate(pageURLs.agentEditwantad, { state: { data: adDetails } });
    }
  };

  const onWantAdDelete = () => {
    setDeleteModalShow(true);
  };

  /**
   * To handle the want-ad delete
   */
  const handleDeleteWantAd = async () => {
    // setLoading(true)
    if (wantAdId) {
      await deleteWantAd(wantAdId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);

          if (response.errorCode === 0 && responseResult) {
            toast.success("Your want-ad has been deleted.");
            setDeleteModalShow(false);
            navigate(pageURLs.agentMyWantAdListings);
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };
  /**
   * Show popup to handle pause want ad
   * @param {*} id
   */
  const onWantAdArchived = (id) => {
    setArchiveModalShow(true);
  };

  /**
   * To handle the want-ad  pause
   */
  const handleArchiveWantAd = async () => {
    if (wantAdId) {
      await archiveWantAd(wantAdId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            toast.dismiss();
            toast.success("Your want-ad has been paused.");
            setArchiveModalShow(false);
            setArchiveStatus(!archiveStatus);
            setReload(reload + 1);
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  /**
   * To handle the want-ad un pause
   */
  const handleUnArchiveWantAd = async (wantAd) => {
    if (wantAd?.wantAdId) {
      if (
        (wantAd?.Buy_or_Rent == 2 && wantAd?.Move_in_TimeLine == 11) ||
        wantAd?.Buy_or_Rent == 3
      ) {
        if (userType == 2) {
          navigate(`${pageURLs.editWantAdForAgents}/${adDetails?.wantAdId}`, {
            state: { data: adDetails, isFromUnPause: true },
          });
        } else {
          navigate(pageURLs.agentEditwantad, {
            state: { data: adDetails, isFromUnPause: true },
          });
        }
      } else {
        await unArchiveWantAd(wantAd?.wantAdId)
          .then((response) => {
            if (response.errorCode == 0) {
              toast.dismiss();
              toast.success("Your paused want-ad has been made public again.");
              setArchiveStatus(!archiveStatus);
              setReload(reload + 1);
            } else {
              console.log("Something went wrong");
            }
          })
          .catch((err) => {
            console.log("Error", err);
          });
      }
    }
  };

  /**
   * To load nice to have list view
   * @param {*} list
   * @param {*} uniqueType
   * @returns
   */
  const loadNiceToHaveList = (list, uniqueType) => {
    return (
      list &&
      list.map((tl) => {
        const id = _.get(tl, "id");
        const itemText = _.get(tl, "text", "");
        const icon = _.get(tl, "icon", "");
        return (
          <div className="nicetohaves-tag" key={id + uniqueType}>
            <div className="nicetohaves-tag-icon">
              <img
                src={
                  icon
                    ? (() => {
                      try {
                        return require("../../images/" + icon);
                      } catch (e) {
                        return PlaceHolderIcon;
                      }
                    })()
                    : PlaceHolderIcon
                }
                style={{ width: 16, height: 16 }}
              />
            </div>
            <span className="wanted-details-itemtext">{itemText}</span>
          </div>
        );
      })
    );
  };

  /**
   * To handle display adTitle
   * @returns
   */
  const loadAdTitle = () => {
    let adTypeTile = "Buy";
    if (adType === LONG_RENT) {
      adTypeTile = "Rent";
    } else if (adType === SHORT_RENT) {
      adTypeTile = "Stay";
    }
    return (
      <>
        {" "}
        <span className="want-ad-tag align-middle green-tag-bg mb-0 f-size-10">
          {adTypeTile}
        </span>
      </>
    );
  };

  /**
   * To handle strengthen want ad details display
   * @returns
   */
  const loadStrengthenDetails = () => {
    if (_.get(adDetails, "isStrengthen", "")) {
      if (adType === BUY) {
        return (
          <>
            {" "}
            {_.get(adDetails, "prchTypeTxt", "") && (
              <li>
                <span className="list-icn-wrap">
                  <img src={PrResIcon} />
                </span>
                {_.get(adDetails, "prchTypeTxt", "")}
                {(_.get(adDetails, "is1031Exchange", "") == "Yes" ||
                  _.get(adDetails, "is1031Exchange", "") == 1) &&
                  " - 1031 exchange"}
              </li>
            )}
            {_.get(adDetails, "finTypeTxt", "") && (
              <li>
                <span className="list-icn-wrap">
                  <img src={FinancingIcon} />
                </span>
                Financing: {_.get(adDetails, "finTypeTxt", "")}
                {(_.get(adDetails, "isPreApprLoan", "") == "Yes" ||
                  _.get(adDetails, "isPreApprLoan", "") == 1) &&
                  "  - pre approved"}
              </li>
            )}
          </>
        );
      } else if (adType === LONG_RENT) {
        return (
          <>
            {" "}
            {_.get(adDetails, "creditScoreText", "") && (
              <li>
                <span className="list-icn-wrap">
                  <img src={FinancingIcon} />
                </span>
                Credit Score : {_.get(adDetails, "creditScoreText", "")}
              </li>
            )}
          </>
        );
      }
    }
  };
  /**
   * To display timeline in details
   * @returns
   */
  const loadTimeLine = () => {
    return loadTimeLineHtml(adType, adTypeBasedOnProperty, adDetails);
  };

  const loadBuyerAgreement = () => {
    return loadBuyerAgreementHtml(adDetails);
  };
  /**
   * To display preferred location
   * @returns
   */
  const loadPreferredLocation = () => {
    return loadPreferredLocationHtml(adDetails);
  };
  /**
   * To display musthaves section
   * @returns
   */
  const loadAdMustHaves = () => {
    return buildWantAdMustHavesHTML({
      adType: adType,
      adTypeBasedOnProperty: adTypeBasedOnProperty,
      formValues: adDetails,
    });
  };

  /**
   * To handle the want-ad favorite/unfavorite
   */
  const handleFavoriteOrUnfavoriteWantAd = async (
    IsFavourite,
    Want_ad_Id,
    clientId = [],
  ) => {
    if (Want_ad_Id) {
      let data = {
        clientId: IsFavourite == false ? clientId : null,
        wantAdId: Want_ad_Id,
      };
      await (
        IsFavourite == false ? favouriteWantAd(data) : unFavouriteWantAd(data)
      )
        .then((response) => {
          if (response.errorCode == 0) {
            let message = "";
            if (clientId.length > 0) {
              message =
                "Want-Ad has been favorited for " +
                clientId.length +
                (clientId.length > 1 ? " clients" : " client");
            } else {
              message =
                IsFavourite == true
                  ? "Want-Ad has been removed from your favorites"
                  : "Want-Ad has been added to your favorites";
            }

            toast.success(message);
            setModalShow(false);
            setIsFavourite(!IsFavourite);
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  /**
   *
   */
  useEffect(() => {
    getAgentsClientsList();
  }, [sessionData?.userInfo?.userId, searchText, reloadAgents]);

  /**
   * To handle textbox value change on agent client search
   * @param {any} event
   */
  const onChangeText = (event) => {
    let value = "";
    if (event) {
      value = handleTextBoxChange(event);
    }

    setSearchText(value);
  };

  /**
   * To get agents clients list
   * @param {*} query
   */
  const getAgentsClientsList = async (query) => {
    let a = {
      search: searchText,
      sortBy: "createdOn",
      sortDir: "desc",
    };
    await getAgentsList(a)
      .then((response) => {
        const responseResult = _.get(response, "result", null);
        if (response.errorCode === 0 && responseResult) {
          setResults(responseResult);
        } else {
          console.log("Something went wrong");
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  /**
   * to handle clients favorites action
   * @param {*} agentIds
   */
  const handleClientFavoriteWantAd = (agentIds) => {
    handleFavoriteOrUnfavoriteWantAd(isFavourite, wantAdId, agentIds);
  };

  /**
   * handle agents favorite action, to open client list popup
   */
  const handleAgentsFavoriteAction = () => {
    setModalShow(true);
  };

  const isNiceToHaveExist = nice2Have.length > 0;
  const renderTooltip = (props) => (
    <Tooltip
      id="button-tooltip"
      placement="bottom-start"
      className="custom-tooltip"
      {...props}
    >
      {propertyType === IS_LAND || propertyType === IS_MULTI_FAMILY
        ? getTooltipText(propertyType)
        : propertySubTypes &&
        propertySubTypes.length > 0 &&
        getCombinedText(propertySubTypes)}
    </Tooltip>
  );

  //HANDLE ZIPCODE POLYGON
  const setBoundaryCoordinates = (zipCode) => {
    if (_.isEmpty(zipCode)) return;

    var payload = [];
    zipCode.map(function (item) {
      payload.push(item?.zipCode);
    });
    getBoundaryCoordinates({ zipCode: payload })
      .then((response) => {
        const responseResult = _.get(response, "result", null);

        if (response.errorCode === 0 && responseResult) {
          let coordinates = [];
          let zipValues = [];
          responseResult.map((result) => {
            zipValues.push({ zipCode: result?.ZipCode, zipId: result?.ZipId });

            coordinates.push([result?.coordinates]);
          });
          setPolygonCoordinates(coordinates);
        } else {
          console.log("Something went wrong");
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  useEffect(() => {
    setBoundaryCoordinates(zipcodes);
  }, [zipcodes]);

  const handleSortParam = (selectedOption) => {
    setSortType(selectedOption.value);
  };

  const renderAuthorProfile = () => {
    const currentUserIsAgent = userType === USER_TYPE_AGENT;
    const isAgentCreated = _.get(adDetails, "isAgentCreated", "") == 1;

    const agentInfoPath = `/agent-info/${agentSlug}`;
    const agentNavState = { state: { canGoBack: true } };

    const agentNavLink = (
      <NavLink
        to={agentInfoPath}
        state={{ canGoBack: true }}
        className="blue-text"
      >
        View bio & contact info
      </NavLink>
    );

    const consumerFallback = currentUserIsAgent ? (
      <>
        <span className="list-icn-wrap">
          <img
            src={ConsumerIcon}
            alt="avatar"
            className="property-agent-profile"
            width="25"
            height="25"
          />
        </span>
        <div className="property-not-agent-name mx-0">
          <span className="timeline-value">{consumerType}</span>
        </div>
      </>
    ) : (
      <>
        <span className="list-icn-wrap">
          <img src={ConsumerIcon} />
        </span>
        {consumerType}{" "}
        {wantAdCreatedUserPlan === INVESTOR_MEMBER_PLAN ? (
          <span className="pro-gold-badge">Investor Member</span>
        ) : (
          ""
        )}
      </>
    );

    return (
      <AgentProfileCard
        isAgentCreated={isAgentCreated && createdUserTypeId !== 1}
        userAvatar={userAvatar}
        userFirstName={_.get(adDetails, "userFirstName", "")}
        userLastName={_.get(adDetails, "userLastName", "")}
        userCompany={userCompany}
        avatarSize="40"
        avatarWrapperClassName="list-icn-wrap"
        onAvatarClick={() => navigate(agentInfoPath, agentNavState)}
        linkComponent={agentNavLink}
        fallback={consumerFallback}
      />
    );
  };

  const MatchesTab = () => {
    const mlsHasData = mlsMatches?.length > 0;

    if (isMatchesLoading) {
      return <MatchesSkeleton />;
    }

    if (!mlsHasData) {
      return (
        <div className="no-result-wrap">
          <h3>No matches found</h3>
          <p>There are no matching MLS listings for this want-listing yet.</p>
        </div>
      );
    }

    const handleShowMoreClick = () => fetchNextMlsPage();

    return (
      <>
        <div className="sort-items-panel">
          <div className="sort-items-inner-wrapper">
            <div className="row">
              <h1 className="">
                Active Listings That Match "{adDetails?.adTitle}"
              </h1>
              <div className="d-flex mb-4">
                <div className="col"></div>
                <div style={{ width: "auto !important" }}>
                  <div className="d-flex select-wrap position-relative gap-3">
                    <Form.Label style={{ fontSize: 15 }}>Sort By:</Form.Label>
                    <SelectDropDown
                      id="multi3"
                      name="colors3"
                      options={options}
                      className="basic-single sort-select d-inline-block mb-0"
                      classNamePrefix="select"
                      placeholder="Select"
                      value={options.find((opt) => opt.value === sortType)}
                      onChange={handleSortParam}
                      styles={{
                        dropdownIndicator: (provided, state) => ({
                          ...provided,
                          transform:
                            state.selectProps.menuIsOpen && "rotate(180deg)",
                        }),
                      }}
                      errorClass="d-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="matches-inner-tab-body">
          {mlsMatches.map((m, index) => (
            <MlsMatchCard
              list={m}
              key={`mls-${m?.mlsListingKey || m?.ListingKey || index}`}
            />
          ))}
          {hasNextMlsPage ? (
            <div className="text-center pt-3 pb-3 w-100">
              <button
                className="btn btn-primary"
                onClick={() => handleShowMoreClick()}
                disabled={isFetchingNextMlsPage}
              >
                {isFetchingNextMlsPage ? "Loading..." : "Show More"}
              </button>
            </div>
          ) : null}
        </div>
      </>
    );
  };

  const { shouldRestrictAgent } = useAgentGitchaRestrictions();
  const { isGitchaDefaultWebsite } = useCurrentWebsite();
  const [showMatchesFeatureModal, setShowMatchesFeatureModal] = useState(false);

  const shouldDisplayShareCard = sessionData?.userInfo?.userId && isMyWantAd

  const { redirectToBLSEnv } = useMLSAgentAtGitchaRedirect();

  useEffect(() => {
    if (shouldRestrictAgent && !isSyndicatedToPublic && adDetails?.slug) {
      redirectToBLSEnv({
        path: `${pageURLs.wantAdDetails}/${adDetails?.slug}`,
        onGitchaEnvFallback: null,
      });
    }
  }, [shouldRestrictAgent, isSyndicatedToPublic, adDetails?.slug]);

  useEffect(() => {
    if (isGitchaDefaultWebsite && tabKey === "Matches") {
      setTabKey("Want-Listing Details");
      setShowMatchesFeatureModal(true);
    }
  }, [isGitchaDefaultWebsite]);

  const dispatch = useDispatch()

  return (
    <>
      <SeoHead adDetails={adDetails} />
      <span ref={pageTopRef}></span>

      {displayTutorial ? (
        <JoyRide
          steps={tutorialSteps}
          tooltipComponent={TutorialToolTip}
          continuous
          callback={handleJoyrideCallback}
          disableScrolling={true}
        />
      ) : (
        ""
      )}

      {skeltonLoad ? (
        <WantadDetailsSkeleton />
      ) : (
        <>

          <PageWrapper>
            {!noWantAdFound && (
              <>
                {" "}
                <div className="main-content p-0 agent-no-login-content">
                  {(isFromHomeFeed || isFromAllIntroduction || introId) && (
                    <BackButton
                      className="mb-24 d-inline-block"
                      onClick={() => {
                        if (isFromConfirmationPage) {
                          navigate(pageURLs.agentMyWantAdListings);
                          return;
                        }
                        // Only step back within the app's own history. idx is 0
                        // when the detail page is the first entry in this tab
                        // (opened directly or from an external site), so going
                        // back would leave Gitcha - fall back to the feed instead.
                        const canGoBackInApp = window.history.state?.idx > 0;
                        navigate(canGoBackInApp ? -1 : pageURLs.home);
                      }}
                    />
                  )}
                </div>
                <div className={isMyWantAd ? "tab-wrap tab-wrap-fix" : ""}>
                  <Tabs
                    className="mb-3"
                    style={isMyWantAd ? {} : { display: "none" }}
                    activeKey={
                      isMyWantAd && !isMatchingInProgress
                        ? tabKey
                        : "Want-Listing Details"
                    }
                    onSelect={(k) => {
                      if (k === "Matches" && isMatchingInProgress) return;
                      if (k === "Matches") {
                        if (isGitchaDefaultWebsite) {
                          setShowMatchesFeatureModal(true);
                          return;
                        }
                        redirectToBLSEnv({ path: `${pageURLs.wantAdDetails}/${adDetails?.slug}?tabKey=Matches`, onGitchaEnvFallback: () => { } });
                      }
                      setTabKey(k);

                      // Sync URL so back-navigation lands on the right tab.
                      // When the user opens an MLS match from inside the
                      // Matches tab and clicks Back from the detail page,
                      // they should return here on the same tab — that only
                      // works if tabKey is in the URL when they leave.
                      const next = new URLSearchParams(searchParams);
                      if (k && k !== "Want-Listing Details") {
                        next.set("tabKey", k);
                      } else {
                        next.delete("tabKey");
                      }
                      setSearchParams(next, { replace: true });
                    }}
                  >
                    <Tab
                      eventKey={"Want-Listing Details"}
                      title={"Want-Listing Details"}
                    >
                      <div className="row">
                        <div className="col-12 col-md-7 ps-md-3 col-lg-8 ps-lg-3 col-xl-8 ps-xl-2" style={{ width: shouldRestrictAgent && !shouldDisplayShareCard ? '100%' : null }}>
                          <div className="wantad-confirmation-wrap mb-4">
                            <div className="confirmation-top-sec d-md-flex justify-content-between">
                              <div className="long-txt-fix">
                                {/* FIRE DISPLACED */}
                                {fireDisplaced ? (
                                  <div className="dis-fires detail">
                                    <img
                                      src={DisplacedFired}
                                      alt="displaced-fired"
                                      className="displaced-fired"
                                      width={"9px"}
                                      height={"11px"}
                                    />
                                    DISPLACED BY FIRES
                                  </div>
                                ) : null}
                                <div className="confirm-sub-title mb-1">
                                  Looking to:
                                  {loadAdTitle()}
                                </div>
                                <h2>
                                  {_.get(
                                    adDetails,
                                    "adTitle",
                                    "Cozy home for small family",
                                  )}{" "}
                                </h2>
                                <span className="trailing-txt">
                                  {locationDisplay(adDetails)}
                                </span>
                              </div>

                              {sessionData?.userInfo?.userId &&
                                WantCreatedUserId !==
                                sessionData?.userInfo?.userId ? (
                                <div className="social-btns-wrap">
                                  <Dropdown className="mr-12">
                                    <Dropdown.Toggle
                                      variant=""
                                      id="dropdown-basic1"
                                    >
                                      <img src={MoreIcon} />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu align="end">
                                      <Dropdown.Item
                                        onClick={() => handleOnShare()}
                                      >
                                        Share
                                      </Dropdown.Item>
                                      {WantCreatedUserId !==
                                        sessionData?.userInfo?.userId && (
                                          <Dropdown.Item
                                            onClick={() =>
                                              handleReportPopup(true)
                                            }
                                          >
                                            Report
                                          </Dropdown.Item>
                                        )}
                                    </Dropdown.Menu>
                                  </Dropdown>
                                  {!shouldRestrictAgent &&
                                    <a
                                      role="button"
                                      href={void 0}
                                      className="social-content-btn"
                                    >
                                      {isFavouriteStatus ? (
                                        <img
                                          src={HearttIconFilled}
                                          onClick={() => {
                                            handleFavoriteOrUnfavoriteWantAd(
                                              true,
                                              wantAdId,
                                            );
                                          }}
                                        />
                                      ) : (
                                        <img
                                          src={HearttIcon}
                                          onClick={() => {
                                            sessionData?.userInfo?.userType ==
                                              2 && isPremiumUser
                                              ? handleAgentsFavoriteAction()
                                              : handleFavoriteOrUnfavoriteWantAd(
                                                false,
                                                wantAdId,
                                              );
                                          }}
                                        />
                                      )}
                                    </a>
                                  }

                                </div>
                              ) : (
                                <>
                                  {sessionData?.userInfo?.userId ? (
                                    <div className="social-btns-wrap">
                                      <a
                                        role="button"
                                        className="social-content-btn mr-12"
                                        onClick={() => handleOnShare()}
                                      >
                                        <img src={ShareIcon} />
                                      </a>
                                      {shouldRestrictAgent ? <></> : (
                                        <Dropdown>
                                          <Dropdown.Toggle
                                            variant=""
                                            id="dropdown-basic1"
                                          >
                                            <img src={MoreIcon} />
                                          </Dropdown.Toggle>
                                          <Dropdown.Menu align="end">
                                            <Dropdown.Item
                                              onClick={() => onEdit()}
                                            >
                                              Edit
                                            </Dropdown.Item>
                                            {adDetails?.isArchived == 0 && (
                                              <Dropdown.Item
                                                onClick={() =>
                                                  onWantAdArchived(wantAdId)
                                                }
                                              >
                                                Pause
                                              </Dropdown.Item>
                                            )}
                                            {adDetails?.isArchived == 1 && (
                                              <Dropdown.Item
                                                onClick={() =>
                                                  handleUnArchiveWantAd(
                                                    adDetails,
                                                  )
                                                }
                                              >
                                                Unpause
                                              </Dropdown.Item>
                                            )}
                                            <Dropdown.Item
                                              onClick={() => onWantAdDelete()}
                                            >
                                              Delete
                                            </Dropdown.Item>
                                          </Dropdown.Menu>
                                        </Dropdown>
                                      )}
                                    </div>
                                  ) : null}
                                </>
                              )}
                            </div>

                            <div className="confirm-map-wrap">
                              {/* COMMENTED OVERLAY FOR NOW*/}
                              <OverlayTrigger
                                placement="bottom-start"
                                delay={{ show: 250, hide: 400 }}
                                overlay={renderTooltip}
                              >
                                <div
                                  className={`property-tile w-auto ${fireDisplaced ? "firedisplaced" : ""}`}
                                  style={{ zIndex: 99 }}
                                >
                                  {propertyType === IS_LAND ||
                                    propertyType === IS_MULTI_FAMILY ? (
                                    <img
                                      src={getPropertyIcon(propertyType)}
                                      alt={getPropertyIcon(propertyType)}
                                      className="property-icon-img"
                                    />
                                  ) : (
                                    propertySubTypes &&
                                    propertySubTypes.length > 0 &&
                                    propertySubTypes.map((subtype) => {
                                      return (
                                        <img
                                          src={getPropertyIcon(subtype)}
                                          alt={getPropertyIcon(subtype)}
                                          className="property-icon-img"
                                        />
                                      );
                                    })
                                  )}
                                </div>
                              </OverlayTrigger>
                              <MapBoxComponent
                                center={[lng, lat]}
                                radiusMiles={drawnPolygon ? null : mapRadius}
                                polygonCoordinates={drawnPolygon ? [[drawnPolygon]] : polygonCoordinates}
                                showPolygon={!!drawnPolygon || zipcodes.length > 0}
                              />
                            </div>
                            {loadAdMustHaves()}

                            {isNiceToHaveExist ? (
                              <div className="nicetohaves-wrap">
                                <h2 className="wanted-details-head">
                                  Nice-to-haves
                                </h2>
                                <div className="nicetohaves-inner-wrap">
                                  {finalNiceToHaveList()}
                                </div>
                              </div>
                            ) : (
                              ""
                            )}

                            {_.get(adDetails, "adDesc", "") ? (
                              <div className="nicetohaves-wrap">
                                <h2 className="wanted-details-head">
                                  Description
                                </h2>
                                <p className="long-txt-fix timeline-value desc-block">
                                  {_.get(adDetails, "adDesc", "")}
                                </p>
                              </div>
                            ) : (
                              ""
                            )}

                            {_.get(adDetails, "videoUrl", "") && (
                              <div className="video-wrap">
                                <video
                                  id="videoPlayer"
                                  controls
                                  width={isVertical ? "300px" : "100%"} // Width 300px for vertical, auto for horizontal
                                  height={isVertical ? "500px" : "auto"} // Auto height for vertical, 500px for horizontal
                                  className="video-player"
                                  preload="metadata"
                                  poster={_.get(
                                    adDetails,
                                    "thumbnailImgUrl",
                                    "",
                                  )}
                                >
                                  <source
                                    src={_.get(adDetails, "videoUrl", "")}
                                    type="video/mp4"
                                  />
                                  Your browser does not support the video tag.
                                </video>
                              </div>
                            )}

                            <div
                              className={
                                (adType == BUY ||
                                  _.get(adDetails, "prprtyType", "") ==
                                  IS_LAND) &&
                                  _.get(adDetails, "isAgentCreated", "") == 1 ? `buyer-info ${adDetails?.otherConcessions ? "nicetohaves-wrap" : ""}` : "buyer-info"
                              }
                            >
                              <h2 className="wanted-details-head">
                                Additional details
                              </h2>
                              <ul>
                                <li>{renderAuthorProfile()}</li>
                                {loadBuyerAgreement()}
                                {loadTimeLine()}
                                {loadPreferredLocation()}
                                {loadStrengthenDetails()}
                              </ul>
                              {isBLSTenant && blsReference && (
                                <div className="detail-meta-info">
                                  <div className="meta-item">
                                    <span className="meta-label">MLS</span>
                                    <span className="meta-value">
                                      {blsMlsName}
                                    </span>
                                  </div>
                                  <div className="meta-item">
                                    <span className="meta-label">BLS ID</span>
                                    <span className="meta-value">
                                      #{blsReference}
                                    </span>
                                  </div>
                                  <div className="meta-item">
                                    <span className="meta-label">Posted</span>
                                    <span className="meta-value">
                                      {blsPostedLabel}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>

                            <OtherConcessionsSection adType={adType} adDetails={adDetails} />

                            {wantAdCreatedUserPlan === INVESTOR_MEMBER_PLAN &&
                              purchaseType === 3 && (
                                <div className="buyer-info">
                                  {_.get(adDetails, "investmentId", [])
                                    ?.length > 0 ||
                                    _.get(adDetails, "capRateId", null) ? (
                                    <h2>What This Investor is Open to</h2>
                                  ) : (
                                    ""
                                  )}
                                  <ul>
                                    {loadInvestmentCriteria(adDetails)}
                                    {_.get(adDetails, "capRateId", null) && (
                                      <li>
                                        <span className="list-icn-wrap">
                                          <img src={MinCapIcon} />
                                        </span>
                                        Minimum cap rate of{" "}
                                        {_.get(adDetails, "capRateId", "") == 15
                                          ? "15+"
                                          : _.get(adDetails, "capRateId", "")}
                                        %
                                      </li>
                                    )}
                                  </ul>
                                </div>
                              )}
                          </div>
                        </div>

                        {!isFromAllIntroduction && (
                          <div className="col-12 col-md-5 pe-md-3 col-lg-4 pe-lg-3 col-xl-4 pe-xl-2">
                            {isMyWantAd && isMatchingInProgress && (
                              <div className="matches-loading-widget mb-4">
                                <span
                                  className="matches-loading-widget__spinner"
                                  aria-hidden="true"
                                />
                                <div className="matches-loading-widget__text">
                                  <h6>Finding your matches</h6>
                                  <p>
                                    We're scanning listings for potential
                                    matches. This won't take long — They'll
                                    appear in the Matches tab once it finishes.
                                  </p>
                                </div>
                              </div>
                            )}
                            {!isMyWantAd ? (
                              <div style={{ marginBottom: 24 }}>
                                <SendMessage wantAd={adDetails} />
                              </div>
                            ) : null}
                            {WantCreatedUserId &&
                              WantCreatedUserId != loggedInUserId &&
                              loggedInUserId ? (
                              <SendIntroduction
                                wantAdId={wantAdId}
                                WantCreatedUserName={WantCreatedUserName}
                                WantCreatedUserId={WantCreatedUserId}
                                wantAdClientId={wantAdClientId}
                                wantAdDetails={adDetails}
                              />
                            ) : isLoadIntroductionList &&
                              introductionList?.length ? (
                              <ViewWantAdIntroductions
                                introCount={introCount}
                                list={introductionList}
                                updateIntroPageOnScroll={
                                  updateIntroPageOnScroll
                                }
                                getIntroductionDetails={getIntroductionDetails}
                              />
                            ) : (
                              <>
                                <div >

                                  {loggedInUserId && !shouldRestrictAgent && (
                                    <div className="ad-strengthen-wrap mb-0 mb-sm-4">
                                      <div className="icon-wrap">
                                        <img
                                          src={AdIntroIcon}
                                          alt={AdIntroIcon}
                                        />
                                      </div>
                                      <h6 className="send-instruction-head">
                                        When you receive a new introduction
                                        you'll see it here
                                      </h6>
                                      <p className="mb-2 wanted-info-listdetail">
                                        Start a message thread by either sending
                                        or responding to an introduction on a
                                        want ad
                                      </p>
                                    </div>
                                  )}

                                  {!loggedInUserId && (
                                    <div className="ad-strengthen-wrap mb-0 mb-sm-4">
                                      <SignedOutIntroCTA />
                                    </div>
                                  )}

                                  {shouldDisplayShareCard && (
                                    <div className="share-card-wrapper share-sidecard">
                                      <ShareCard
                                        wantAdId={wantAdId}
                                        isSideCard={true}
                                        adDetails={adDetails}
                                        isWantAdCreated={isWantAdCreated}
                                        shareLink={shareUrl}
                                        fromWantAdDetails={true}
                                        imageLink={imageUrl}
                                        fbLink={fbUrl}
                                      />
                                    </div>
                                  )}

                                </div>

                                {flags?.mlsAgentEditor && !isSyndicatedToPublic && isMyWantAd && (
                                  <div className="ad-strengthen-wrap mb-4">
                                    <div className="icon-wrap">
                                      <img
                                        src={PaperIcon}
                                        alt={PaperIcon}
                                      />
                                    </div>
                                    <h4>
                                      Syndicate to Gitcha
                                    </h4>
                                    <p className="mb-4">
                                      Publish this want-listing to Gitcha to unlock sharing, and exposure beyond the MLS. You stay in control and can update or remove it anytime.
                                    </p>
                                    <Button
                                      variant="primary"
                                      className="w-100 mb-4"
                                      style={{ fontSize: 14, fontWeight: 700, letterSpacing: 'normal' }}
                                      onClick={() => { setShowSyndicateModal(true) }}
                                    >
                                      Syndicate to Gitcha
                                    </Button>
                                  </div>
                                )}

                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </Tab>
                    <Tab
                      eventKey={"Matches"}
                      disabled={isMatchingInProgress}
                      title={
                        isMatchingInProgress ? (
                          <span
                            className="matches-matching-label"
                            aria-label="Potential matches are being calculated — they'll appear here automatically"
                            title="Potential matches are being calculated — they'll appear here automatically"
                          >
                            Matches{" "}
                            <span
                              className="matches-matching-wave"
                              aria-hidden="true"
                            >
                              <span />
                              <span />
                              <span />
                            </span>
                          </span>
                        ) : !adDetails?.wantAdId || isMatchesLoading ? (
                          <>
                            Matches{" "}
                            <span
                              className="mls-inline-spinner"
                              aria-hidden="true"
                            />
                          </>
                        ) : (
                          `Matches (${totalMlsMatches || 0})`
                        )
                      }
                    >
                      <MatchesTab />
                    </Tab>
                  </Tabs>
                </div>
              </>
            )}
          </PageWrapper>

          {!noWantAdFound && showShareModal && loggedInUserId && (
            <ShareYourAdModal
              show={showShareModal}
              onHide={handleClose}
              adDetails={adDetails}
              isWantAdCreated={isWantAdCreated}
              shareLink={shareUrl}
              imageLink={imageUrl}
              fbLink={fbUrl}
              fromWantAdDetails={true}
            />
          )}

          <ShareMyWantAdSmallModal
            show={showSuccessModal}
            onHide={handleSuccessModalClose}
            showShareModalPopup={showShareModalPopup}
          />
          <NoWantAdFoundModal show={noWantAdFound} />
          <ReportWantAdModal
            show={showReportModal}
            wantAdId={wantAdId}
            onHide={() => handleReportPopup(false)}
          />
          {introDetails.length > 0 && (
            <ViewIntroductionModal
              details={introDetails?.[0]}
              show={showViewIntroductionModal}
              onHide={handleIntroDetailModal}
            />
          )}
          {/*  Add to client favorite Modal */}
          <AddToClientWantAdModal
            show={modalShow}
            showAddToClientWantAdModal={() => setModalShow(true)}
            onHide={() => setModalShow(false)}
            data={results}
            handleSkip={() =>
              handleFavoriteOrUnfavoriteWantAd(isFavourite, wantAdId)
            }
            handleConfirm={handleClientFavoriteWantAd}
            searchText={searchText}
            onChangeText={(e) => onChangeText(e)}
            onAddClient={() => setReloadAgents(reloadAgents + 1)}
          />
          <DeleteModal
            show={deleteModalShow}
            onHide={() => setDeleteModalShow(false)}
            title={"Are you sure you want to permanently delete your want-ad?"}
            message={
              "Deleting this ad will stop any further introductions from being sent to you."
            }
            buttonText={" Delete Want-Ad"}
            handleConfirm={() => handleDeleteWantAd()}
          />
          <ArchiveModal
            show={archiveModalShow}
            onHide={() => setArchiveModalShow(false)}
            handleConfirm={() => handleArchiveWantAd()}
          />
          <AgentProfileViewModal
            show={showAgentProfileViewModal}
            onClose={agentProfileViewModalClose}
            userId={_.get(adDetails, "userId", "")}
            isTitleShow={true}
          />
          <PremiumPlanModal
            userDetails={userDetailsFromStore[0]}
            show={showPremiumPlanModal}
            onHide={PremiumPlanModalClose}
            showPlanModal={PremiumPlanModalShow}
          />
          <SyndicateListingModal
            show={showSyndicateModal}
            onHide={() => setShowSyndicateModal(false)}
            syndicateListing={() => handleSyndicateWantAd(_.get(adDetails, "wantAdId", ""))}
          />
          <InventorySuccessModal
            show={showSyndicateSuccessModal}
            onHide={() => setSyndicateSuccessModal(false)}
            successPopUpTitle={"Want-Listing syndicated successfully"}
            successPopUpDesc={"Your Want-Listing has been syndicated to Gitcha."}
          />
          <MatchesFeatureModal
            show={showMatchesFeatureModal}
            onHide={() => setShowMatchesFeatureModal(false)}
          />
        </>
      )}
    </>
  );
};

export default WantadDetails;
